import { writable, get } from "svelte/store";
import { GQLNow } from "gql-now";
import { ADMIN_SERVICES_URL, ORG_ID, getAdminToken, getAdminUser } from "./auth.js";

/**
 * Resilient Fallback Defaults (mirrors backend SystemDocsCatalog SSOT)
 */
export const DEFAULT_NAV_SECTIONS = [
  {
    id: "knowledge-governance",
    title: "System Knowledge & Governance",
    icon: "f7:book_fill",
    color: "#2563eb",
    description: "Operational system guide, architectural documentation, and compliance audits",
    items: [
      {
        title: "Broker & Back-Office Ops",
        route: "/system-docs/",
        icon: "f7:book_fill",
        badge: "NEW",
        badge_color: "blue",
        description: "Complete operational evolution guide: manual to automated back-office transformation",
        roles: ["org_admin", "executive", "dealer"],
      },
      {
        title: "F1F2 Autopilot Migration",
        route: "/f1f2-migration-guide/",
        icon: "f7:rocket_fill",
        badge: "AUTOPILOT",
        badge_color: "orange",
        description: "From 50-tab browser nightmare to 24/7 autonomous cloud Go engine & unified dealer terminal",
        roles: ["org_admin", "dealer", "executive"],
      },
    ],
  },
  {
    id: "trading-dealer-hub",
    title: "Trading & Dealer Hub",
    icon: "f7:chart_bar_alt_fill",
    color: "#059669",
    description: "Direct order execution, trading terminals, scrip masters, and strategy engines",
    items: [
      {
        title: "Live Dealer Terminal",
        route: "/dealer-terminal/",
        icon: "f7:bolt_fill",
        badge: "LIVE",
        badge_color: "green",
        description: "High-speed multi-client intraday execution and order book view",
        roles: ["org_admin", "dealer"],
      },
      {
        title: "Client Trading Controls",
        route: "/user-trading-controls/",
        icon: "f7:slider_horizontal_3",
        description: "Per-user square-off limits, max drawdown caps, and trading permissions",
        roles: ["org_admin", "dealer"],
      },
      {
        title: "Strategy & Risk Manager",
        route: "/strategy-manager/",
        icon: "f7:chart_bar_alt_fill",
        description: "Quant strategy weights, active allocations, and stop-loss rules",
        roles: ["org_admin", "dealer"],
      },
      {
        title: "Scrip Master & Tokens",
        route: "/master-data-manager/",
        icon: "f7:folder_fill",
        description: "NSE/BSE scrip master tokens, tick sizes, and lot configurations",
        roles: ["org_admin", "dealer"],
      },
      {
        title: "Market Calendar",
        route: "/market-calendar/",
        icon: "f7:calendar",
        description: "Trading sessions, exchange clearing holidays, and corporate events",
        roles: ["org_admin", "dealer"],
      },
      {
        title: "Bulk Orders CSV",
        route: "/dealer-bulk-orders/",
        icon: "f7:tray_arrow_up_fill",
        description: "Batch multi-account execution via pre-formatted order spreadsheets",
        roles: ["org_admin", "dealer"],
      },
      {
        title: "Mutual Funds & SIPs",
        route: "/mutual-fund-portfolio/",
        icon: "f7:graph_square",
        description: "Client mutual fund portfolios, NAV tracking, and SIP schedules",
        roles: ["org_admin", "dealer"],
      },
      {
        title: "BSE Conformance",
        route: "/bse-conformance/",
        icon: "f7:checkmark_shield_fill",
        badge: "23_FEDE",
        badge_color: "orange",
        description: "Exchange automated conformance suite and order book simulators",
        roles: ["org_admin", "dealer"],
      },
    ],
  },
  {
    id: "user-management",
    title: "User Management & Approvals",
    icon: "f7:person_2_fill",
    color: "#6366f1",
    description: "Account verification, FCML broker onboarding, and demat allocation",
    items: [
      {
        title: "Approve Account & FCML",
        route: "/approve-account/",
        icon: "f7:person_badge_plus_fill",
        badge: "ACTION",
        badge_color: "green",
        description: "Targeted 16-digit CDSL Demat allocation and direct FCML broker onboarding",
        roles: ["org_admin"],
      },
      {
        title: "All Users Directory",
        route: "/users/",
        icon: "f7:person_2_fill",
        description: "Complete searchable database of registered TRDR clients",
        roles: ["org_admin"],
      },
      {
        title: "Approved Users",
        route: "/approved-users/",
        icon: "f7:person_crop_circle_badge_checkmark",
        description: "Active accounts with verified CDSL Demat and trading authorization",
        roles: ["org_admin"],
      },
      {
        title: "Pending Approvals",
        route: "/pending-users/",
        icon: "f7:person_crop_circle_badge_exclamationmark",
        description: "Applicants awaiting KYC review and CDSL Demat generation",
        roles: ["org_admin"],
      },
      {
        title: "Incomplete Signups",
        route: "/incomplete-users/",
        icon: "f7:person_crop_circle_badge_minus",
        description: "Users who dropped off during registration or mobile verification",
        roles: ["org_admin"],
      },
      {
        title: "Admin Directory",
        route: "/admin-users-list/",
        icon: "f7:shield_lefthalf_fill",
        description: "Authorized admin accounts, emails, mobiles, and security permissions",
        roles: ["org_admin"],
      },
    ],
  },
  {
    id: "risk-financials",
    title: "Financial Audits & Risk Controls",
    icon: "f7:creditcard_fill",
    color: "#8b5cf6",
    description: "Payment reconciliation, capital leak monitoring, and statutory reporting",
    items: [
      {
        title: "Cashfree Payment Audits",
        route: "/cashfree-transactions/",
        icon: "f7:creditcard_fill",
        badge: "RECONCILED",
        badge_color: "purple",
        description: "Payment gateway audit with test-account filtering and RFC-4180 CSV export",
        roles: ["org_admin", "executive"],
      },
      {
        title: "Homepage Financial Health",
        route: "/all-users-home-data/",
        icon: "f7:gauge_badge_plus",
        badge: "MONITOR",
        badge_color: "orange",
        description: "Live client AUM, negative ledger anomaly detection, and capital leak alerts",
        roles: ["org_admin", "executive"],
      },
      {
        title: "Unified Multi-Report Hub",
        route: "/all-reports-of-user/",
        icon: "f7:doc_text_fill",
        description: "Tabular flattening of Net Position, Ledger, Portfolio, and Long/Short",
        roles: ["org_admin", "executive"],
      },
      {
        title: "Withdrawal Requests",
        route: "/withdraw-requests/",
        icon: "f7:arrow_down_circle_fill",
        description: "Client payout requests, bank clearance, and ledger debit verification",
        roles: ["org_admin"],
      },
      {
        title: "ENACH Mandates & Debits",
        route: "/all-enach/",
        icon: "f7:arrow_clockwise_circle_fill",
        description: "Sponsor bank mandate registration, periodic debits, and NACH logs",
        roles: ["org_admin"],
      },
      {
        title: "Penny Drop Verification",
        route: "/verify-penny-drop/",
        icon: "f7:checkmark_circle_fill",
        description: "Instant 1-rupee penny drop verification of client bank accounts",
        roles: ["org_admin"],
      },
      {
        title: "Referrals & Growth",
        route: "/referrals/",
        icon: "f7:person_3_fill",
        description: "Multi-tier affiliate tree, promoter champions, and conversion metrics",
        roles: ["org_admin"],
      },
    ],
  },
];

export const DEFAULT_QUICK_ACTIONS = [
  {
    title: "Broker Ops Guide",
    route: "/system-docs/",
    icon: "f7:book_fill",
    color: "blue",
    badge: "NEW",
  },
  {
    title: "F1F2 Autopilot",
    route: "/f1f2-migration-guide/",
    icon: "f7:rocket_fill",
    color: "orange",
    badge: "AUTOPILOT",
  },
  {
    title: "Approve Users",
    route: "/approve-account/",
    icon: "f7:person_badge_plus_fill",
    color: "green",
    badge: "ACTION",
  },
  {
    title: "Payment Audits",
    route: "/cashfree-transactions/",
    icon: "f7:creditcard_fill",
    color: "purple",
  },
  {
    title: "Client Health",
    route: "/all-users-home-data/",
    icon: "f7:gauge_badge_plus",
    color: "orange",
    badge: "ALERT",
  },
  {
    title: "Dealer Terminal",
    route: "/dealer-terminal/",
    icon: "f7:bolt_fill",
    color: "teal",
    badge: "LIVE",
  },
];

export const navSections = writable(DEFAULT_NAV_SECTIONS);
export const quickActions = writable(DEFAULT_QUICK_ACTIONS);
export const navLoading = writable(false);
export const navLoaded = writable(false);
export const navError = writable(null);

/**
 * Fetch dynamic navigation tree from GraphQL backend with cached fallback
 */
export async function fetchAdminNavigation(forceRefresh = false) {
  if (get(navLoaded) && !forceRefresh) {
    return get(navSections);
  }

  navLoading.set(true);
  navError.set(null);

  try {
    const token = getAdminToken();
    const user = getAdminUser();
    const role = (user && user.role) || "org_admin";

    const headers = {
      "x-trdr-org": ORG_ID,
    };
    if (token) {
      headers["x-access-token"] = token;
    }

    const client = new GQLNow(ADMIN_SERVICES_URL, headers);
    const queryStr = `
      status
      sections {
        id
        title
        icon
        color
        description
        items {
          title
          route
          icon
          badge
          badge_color
          description
          roles
        }
      }
      quick_actions {
        title
        route
        icon
        color
        badge
      }
    `;

    const response = await client.query("fetch_admin_navigation", { role }, queryStr);
    if (response && response.data && response.data.fetch_admin_navigation) {
      const data = response.data.fetch_admin_navigation;
      if (data.sections && data.sections.length > 0) {
        navSections.set(data.sections);
      }
      if (data.quick_actions && data.quick_actions.length > 0) {
        quickActions.set(data.quick_actions);
      }
      navLoaded.set(true);
    }
  } catch (err) {
    console.warn("fetchAdminNavigation: backend offline or error, using SSOT defaults", err);
    navError.set(err.message || String(err));
    // Fallback gracefully ensures zero downtime
    navSections.set(DEFAULT_NAV_SECTIONS);
    quickActions.set(DEFAULT_QUICK_ACTIONS);
    navLoaded.set(true);
  } finally {
    navLoading.set(false);
  }

  return get(navSections);
}

/**
 * Fetch structured system evolution documentation from GraphQL backend
 */
export async function fetchSystemEvolutionDocs(sectionId = null) {
  try {
    const token = getAdminToken();
    const headers = {
      "x-trdr-org": ORG_ID,
    };
    if (token) {
      headers["x-access-token"] = token;
    }

    const client = new GQLNow(ADMIN_SERVICES_URL, headers);
    const queryStr = `
      status
      metadata {
        title
        subtitle
        short_title
        version
        source_doc
        last_updated
        executive_summary
        total_tasks
      }
      scorecards {
        label
        value
        change
        direction
        description
        category
      }
      chapters {
        id
        task_number
        title
        category
        tagline
        executive_summary
        technical_summary
        risk_level_before
        risk_level_now
        turnaround_before
        turnaround_now
        compliance_tags
        before {
          headline
          description
          pain_points
          image_url
          image_caption
          code_snippet
        }
        now {
          headline
          description
          key_improvements
          screenshot_route
          live_route
          action_label
          tech_stack
          code_snippet
        }
      }
    `;

    const args = sectionId ? { section_id: sectionId } : {};
    const response = await client.query("fetch_system_evolution_docs", args, queryStr);
    if (response && response.data && response.data.fetch_system_evolution_docs) {
      return response.data.fetch_system_evolution_docs;
    }
  } catch (err) {
    console.warn("fetchSystemEvolutionDocs: network issue, using cached data", err);
  }
  return null;
}

/**
 * Fetch structured F1F2 to TRDR Autopilot migration documentation from GraphQL backend
 */
export async function fetchF1F2MigrationDocs(transformationId = null) {
  try {
    const token = getAdminToken();
    const headers = {
      "x-trdr-org": ORG_ID,
    };
    if (token) {
      headers["x-access-token"] = token;
    }

    const client = new GQLNow(ADMIN_SERVICES_URL, headers);
    const queryStr = `
      status
      metadata {
        title
        subtitle
        short_title
        version
        last_updated
        executive_summary
        total_transformations
        metrics {
          label
          value
          subtext
          badge
          badge_color
        }
      }
      transformations {
        id
        number
        title
        tagline
        category
        f1f2_before {
          headline
          description
          pain_points
          code_snippet
          risk_level
          speed
          icon
        }
        trdr_now {
          headline
          description
          key_benefits
          code_snippet
          architecture_specs
          action_route
          action_label
          risk_level
          speed
          icon
        }
        dealer_explanation
        tech_explanation
      }
      faqs {
        id
        question
        answer
        category
        role_hint
      }
      code_comparison {
        title
        f1f2_code
        f1f2_caption
        trdr_code
        trdr_caption
      }
    `;

    const args = transformationId ? { transformation_id: transformationId } : {};
    const response = await client.query("fetch_f1f2_migration_docs", args, queryStr);
    if (response && response.data && response.data.fetch_f1f2_migration_docs) {
      return response.data.fetch_f1f2_migration_docs;
    }
  } catch (err) {
    console.warn("fetchF1F2MigrationDocs: network issue, using fallback data", err);
  }
  return null;
}
